import type { CSSProperties } from 'react';

export type CaptionStyleId = 'tiktok' | 'clean' | 'karaoke';

export type CaptionStyle = {
  id: CaptionStyleId;
  name: string;
  hint: string;
  /** How aggressively to group words into one on-screen page. */
  combineWithinMs: number;
  /** Words per page cap — long pages get split by the composition. */
  maxCharsPerPage: number;
};

export const CAPTION_STYLES: CaptionStyle[] = [
  {
    id: 'tiktok',
    name: 'Word pop',
    hint: 'One phrase at a time, active word scales up',
    combineWithinMs: 1200,
    maxCharsPerPage: 26,
  },
  {
    id: 'karaoke',
    name: 'Karaoke fill',
    hint: 'Whole line visible, spoken words fill in',
    combineWithinMs: 2400,
    maxCharsPerPage: 42,
  },
  {
    id: 'clean',
    name: 'Broadcast',
    hint: 'Lower-third bar, no word highlighting',
    combineWithinMs: 2800,
    maxCharsPerPage: 56,
  },
];

export const getStyle = (id: CaptionStyleId): CaptionStyle =>
  CAPTION_STYLES.find((s) => s.id === id) ?? CAPTION_STYLES[0];

/**
 * Font size is expressed relative to the video height so that captions look
 * the same on a 720p phone clip and a 4K export.
 */
export const captionFontSize = (id: CaptionStyleId, height: number): number => {
  const ratio = id === 'clean' ? 0.045 : id === 'karaoke' ? 0.055 : 0.075;
  return Math.round(height * ratio);
};

export const strokeShadow = (px: number): CSSProperties['textShadow'] =>
  [
    `${px}px ${px}px 0 #000`,
    `-${px}px ${px}px 0 #000`,
    `${px}px -${px}px 0 #000`,
    `-${px}px -${px}px 0 #000`,
    `0 ${px}px 0 #000`,
    `0 -${px}px 0 #000`,
    `${px}px 0 0 #000`,
    `-${px}px 0 0 #000`,
  ].join(', ');
